// ============================================================================
// OBT F3 — İnceleyenin yayın listesi (gerçek kuyruk sayaçlarıyla)
// ============================================================================
// Her yayın kartı, aktörün inceleme kuyruğundan türetilen GERÇEK sayaçları
// gösterir. Sahte/örnek sayaç yoktur. Filtreler istemci tarafında yalnız
// gerçek verilere göre uygulanır.
// ============================================================================

import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, RotateCcw, Clock, TreePine, ChevronRight } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState } from '../ObtStates';

const FILTERS = Object.freeze([
  { key: 'all', label: 'Tümü' },
  { key: 'pending', label: 'İnceleme Bekleyen' },
  { key: 'resubmitted', label: 'Yeniden Gönderilen' },
  { key: 'late', label: 'Süresi Geçen' },
]);

function matchesFilter(pub, filter) {
  switch (filter) {
    case 'pending':
      return pub.counts.actionable > 0;
    case 'resubmitted':
      return pub.counts.resubmitted > 0;
    case 'late':
      return Boolean(pub.due_passed);
    default:
      return true;
  }
}

function CountBadge({ icon: Icon, value, label, tone = 'info' }) {
  const tones = {
    info: 'bg-blue-50 text-blue-700 ring-blue-200',
    warning: 'bg-amber-50 text-amber-800 ring-amber-200',
    danger: 'bg-red-50 text-red-700 ring-red-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="font-semibold">{value}</span> {label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {{loading: boolean, error: object|null, publications: Array}} props.state
 * @param {(pub: object) => void} props.onOpen
 * @param {(pub: object) => void} [props.onOpenSuggestions]
 * @param {() => void} props.onReload
 * @param {boolean} [props.showSuggestions]  BM için öneri kısayolu göster
 */
export default function ReviewerPublicationList({
  state,
  onOpen,
  onOpenSuggestions,
  onReload,
  showSuggestions = false,
}) {
  const [filter, setFilter] = useState('all');
  const { loading, error, publications } = state;

  const visible = useMemo(
    () => publications.filter((p) => matchesFilter(p, filter)),
    [publications, filter]
  );

  if (loading) return <ObtLoading label="Yayınlar yükleniyor…" />;
  if (error) return <ObtErrorState error={error} onRetry={onReload} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Yayın filtreleri">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {visible.length === 0 ? (
        <ObtEmptyState
          title="Bu kapsamda görev yok"
          description="Seçili filtreye uyan, incelemenizi bekleyen yayın bulunmuyor."
        />
      ) : (
        <div className="space-y-3">
          {visible.map((pub) => (
            <Card key={String(pub.publication_id)}>
              <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {pub.campaign_title}
                    </h4>
                    {pub.campaign_topic && (
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {pub.campaign_topic}
                      </span>
                    )}
                    {pub.due_passed && (
                      <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
                        <Clock className="h-3 w-3" aria-hidden="true" /> Süresi geçti
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <CountBadge
                      icon={ClipboardList}
                      value={pub.counts.actionable}
                      label="inceleme bekliyor"
                    />
                    {pub.counts.resubmitted > 0 && (
                      <CountBadge
                        icon={RotateCcw}
                        value={pub.counts.resubmitted}
                        label="yeniden gönderildi"
                        tone="warning"
                      />
                    )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-wrap gap-2">
                  {showSuggestions && onOpenSuggestions && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenSuggestions(pub)}
                    >
                      <TreePine className="mr-1.5 h-4 w-4" aria-hidden="true" /> Park Önerileri
                    </Button>
                  )}
                  <Button size="sm" onClick={() => onOpen(pub)}>
                    Cevapları İncele
                    <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
